import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth, startGoogleSignIn } from "@/lib/auth";
import { EntShell, Btn, Field, inputCls, Kicker, errText } from "@/components/enterprise/Ent";

export default function EnterpriseAuth() {
  const [params] = useSearchParams();
  const next = params.get("next") || "/enterprise/dashboard";
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const payload = mode === "login" ? { email: form.email, password: form.password } : form;
      await api.post(`/enterprise/auth/${mode === "login" ? "login" : "register"}`, payload, { withCredentials: true });
      await refresh();
      navigate(next, { replace: true });
    } catch (e2) {
      setErr(errText(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <EntShell>
      <section className="max-w-md mx-auto px-6 py-16">
        <Kicker>Organization administrators</Kicker>
        <h1 className="font-display text-4xl mt-3" data-testid="ent-auth-title">{mode === "login" ? "Sign in" : "Create your account"}</h1>
        <p className="mt-2 text-[15px] text-[#3b3530]">Manage your events, facilitator guides, and Team Impact Reports.</p>

        <div role="tablist" aria-label="Sign in or create account" className="mt-8 grid grid-cols-2 border border-[#bdb3a6] rounded-md overflow-hidden">
          {["login", "register"].map((m) => (
            <button key={m} role="tab" aria-selected={mode === m} onClick={() => { setMode(m); setErr(""); }}
              className={`py-2.5 text-sm font-semibold ${mode === m ? "bg-[#16130f] text-white" : "bg-white"}`} data-testid={`ent-auth-tab-${m}`}>
              {m === "login" ? "Sign in" : "Create account"}
            </button>
          ))}
        </div>

        <form onSubmit={submit} className="mt-6 space-y-4" noValidate>
          {mode === "register" && (
            <Field id="ent-name" label="Your name">
              <input id="ent-name" className={inputCls} value={form.name} onChange={set("name")} autoComplete="name" required data-testid="ent-auth-name" />
            </Field>
          )}
          <Field id="ent-email" label="Work email">
            <input id="ent-email" type="email" className={inputCls} value={form.email} onChange={set("email")} autoComplete="email" required data-testid="ent-auth-email" />
          </Field>
          <Field id="ent-password" label="Password" hint={mode === "register" ? "At least 8 characters." : undefined}>
            <input id="ent-password" type="password" className={inputCls} value={form.password} onChange={set("password")}
              autoComplete={mode === "login" ? "current-password" : "new-password"} required minLength={8} data-testid="ent-auth-password" />
          </Field>
          {err && <p role="alert" className="text-sm text-[#7a1010] font-semibold" data-testid="ent-auth-error">{err}</p>}
          <Btn type="submit" className="w-full" disabled={busy} data-testid="ent-auth-submit">
            {busy ? "Please wait…" : mode === "login" ? "Sign in" : "Create account"}
          </Btn>
        </form>

        <div className="my-6 flex items-center gap-3 text-xs text-[#5a524b]"><span className="flex-1 h-px bg-[#ddd5c9]" />or<span className="flex-1 h-px bg-[#ddd5c9]" /></div>
        <Btn variant="outline" className="w-full" onClick={() => startGoogleSignIn(next)} data-testid="ent-auth-google">Continue with Google</Btn>
      </section>
    </EntShell>
  );
}
